import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { listFeedEvents, MOCK_FEED_ROTATION_MS } from '@/api/events'
import { mockState } from '@/api/mock-data'
import { useTranslation } from 'react-i18next'
import { USE_MOCK } from '@/config'
import { formatMoscowTime } from '@/utils/format'
import { formatTickerChipLine } from '@/utils/ticker-event-text'
import type { FeedEvent } from '@/types/order'
import type { OrderStatus } from '@/types/enums'
import { StatusBadge } from '@/components/orders/StatusBadge'

const MAX_CHIPS = 10

/** Задержка удаления узла после анимации выхода (>= --stryzh-motion-duration-ticker-exit 350ms) */
const TICKER_REMOVAL_MS = 400

/**
 * Возвращает до MAX_CHIPS событий, отсортированных от нового к старому.
 * Трек — flex-direction: row-reverse; justify-content: flex-start.
 * Первый элемент массива = самый правый в UI (новейший).
 * Removing-чипы добавляются в конец массива → оказываются левее всех.
 */
function orderedChipsNewestFirst(data: FeedEvent[]): FeedEvent[] {
  return [...data]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, MAX_CHIPS)
}

function resolveTickerLine(ev: FeedEvent): string {
  if (!USE_MOCK) return ev.summary
  // В mock-режиме для красивой строки нужен контекст заказа (бренд/модель/год).
  const order = mockState.orders.find((o) => o.id === ev.order_id)
  return formatTickerChipLine(ev, order)
}

type Row = { ev: FeedEvent; phase: 'enter' | 'visible' | 'removing' }

function EventTickerChip({
  row,
  displayLine,
  onVisible,
}: {
  row: Row
  displayLine: string
  onVisible: (id: string) => void
}) {
  const ref = useRef<HTMLAnchorElement>(null)
  const { ev } = row

  useLayoutEffect(() => {
    if (row.phase !== 'enter') return
    void ref.current?.offsetWidth
    const rafId = window.requestAnimationFrame(() => {
      onVisible(ev.id)
    })
    return () => window.cancelAnimationFrame(rafId)
  }, [row.phase, ev.id, onVisible])

  const cls =
    row.phase === 'removing'
      ? 'stryzh-event-chip stryzh-event-chip--removing'
      : row.phase === 'visible'
        ? 'stryzh-event-chip stryzh-event-chip--visible'
        : 'stryzh-event-chip'

  return (
    <Link ref={ref} to={`/orders/${ev.order_id}`} className={cls}>
      <div className="stryzh-event-chip__row">
        <span className="stryzh-event-chip__time">{formatMoscowTime(ev.created_at)}</span>
        <span className="stryzh-event-chip__text" title={displayLine}>
          {displayLine}
        </span>
        {ev.status ? (
          <span className="stryzh-event-chip__badge-wrap">
            <StatusBadge status={ev.status as OrderStatus} />
          </span>
        ) : null}
      </div>
    </Link>
  )
}

export function EventTicker() {
  const { t } = useTranslation()
  const { data: rawData } = useQuery({
    queryKey: ['feed-events'],
    queryFn: listFeedEvents,
    /** Демо в моке: периодически сдвигаем окно событий, чтобы чипы перестраивались с анимацией */
    refetchInterval: USE_MOCK ? MOCK_FEED_ROTATION_MS : false,
  })
  // Stabilize reference: default [] only changes when rawData changes
  const data = useMemo(() => rawData ?? [], [rawData])

  const targetList = useMemo(() => orderedChipsNewestFirst(data), [data])
  const lineById = useMemo(() => {
    const m = new Map<string, string>()
    for (const ev of data) {
      m.set(ev.id, resolveTickerLine(ev))
    }
    return m
  }, [data])

  const [rows, setRows] = useState<Row[]>([])
  const removalTimers = useRef<Map<string, number>>(new Map())

  useEffect(() => {
    const timers = removalTimers.current
    return () => {
      for (const tid of timers.values()) {
        window.clearTimeout(tid)
      }
      timers.clear()
    }
  }, [])

  useEffect(() => {
    const targetIds = new Set(targetList.map((e) => e.id))

    // Отмена таймеров для чипов, вернувшихся в target
    for (const id of targetIds) {
      const tid = removalTimers.current.get(id)
      if (tid != null) {
        window.clearTimeout(tid)
        removalTimers.current.delete(id)
      }
    }

    setRows((prev) => {
      if (targetList.length === 0 && prev.length === 0) return prev
      const next: Row[] = []

      // 1. Текущие target-чипы: новейший первым (index 0 = правый в row-reverse)
      for (const ev of targetList) {
        const old = prev.find((x) => x.ev.id === ev.id && x.phase !== 'removing')
        if (old) {
          next.push({ ev, phase: old.phase === 'enter' ? 'enter' : 'visible' })
        } else {
          next.push({ ev, phase: 'enter' })
        }
      }

      // 2. Уже-removing чипы (ещё не удалены из DOM) — в конец массива = визуально левее всех
      for (const r of prev) {
        if (r.phase === 'removing' && !targetIds.has(r.ev.id)) {
          next.push(r)
        }
      }

      // 3. Только что ставшие removing — тоже в конец = левее всех
      for (const r of prev) {
        if (r.phase === 'removing') continue
        if (!targetIds.has(r.ev.id)) {
          next.push({ ...r, phase: 'removing' })
          const id = r.ev.id
          const tid = window.setTimeout(() => {
            removalTimers.current.delete(id)
            setRows((p) => p.filter((x) => x.ev.id !== id))
          }, TICKER_REMOVAL_MS)
          removalTimers.current.set(id, tid)
        }
      }

      return next
    })
  }, [targetList])

  const makeVisible = useCallback((id: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.ev.id === id && r.phase === 'enter' ? { ...r, phase: 'visible' } : r,
      ),
    )
  }, [])

  return (
    <div className="stryzh-event-ticker pointer-events-auto" role="region" aria-label={t('events.regionAria')}>
      <div className="stryzh-event-ticker__row">
        <div className="stryzh-event-ticker__label">
          <span className="stryzh-event-ticker__dot" aria-hidden />
          {t('events.title')}
        </div>
        <div className="stryzh-event-ticker__viewport">
          {rows.length === 0 ? (
            <span className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
              {t('common.noData')}
            </span>
          ) : (
            <div className="stryzh-event-ticker__track">
              {rows.map((row) => (
                <EventTickerChip
                  key={row.ev.id}
                  row={row}
                  displayLine={lineById.get(row.ev.id) ?? resolveTickerLine(row.ev)}
                  onVisible={makeVisible}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
